import { Link } from 'react-router-dom';

import { ArrowBackIcon } from '@/components/icons/registry';
import { buttonVariants } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { cn } from '@/utils/helpers/cn';

import type { ReactNode } from 'react';

interface PageHeaderProps {
  title: string;
  description?: string;
  /** Rota do botão voltar. Omitido, o botão não aparece. */
  backTo?: string;
  /**
   * Voltar como ação, para passo de fluxo que não é rota (ex.: assistente em
   * etapas). Ignorado quando `backTo` existe — voltar por rota é o padrão, e o
   * caso de callback é a exceção que precisa se justificar na tela.
   */
  onBack?: () => void;
  actions?: ReactNode;
  className?: string;
}

/**
 * Cabeçalho de página: voltar, título, apoio e slot de ações.
 *
 * `backTo` é uma rota e não um `onClick`, de propósito. Um botão que chama
 * `navigate(-1)` depende do histórico: quem abriu a tela por link direto,
 * notificação ou atalho da home do PWA — casos comuns aqui — volta para fora do
 * app ou não volta a lugar nenhum. Com destino explícito o caminho é o mesmo
 * sempre.
 *
 * Como é um `<Link>` e não um `<Button>`, as classes vêm de `buttonVariants`:
 * é para isso que o cva é exportado, em vez de duplicar as medidas do alvo de
 * toque num segundo lugar.
 */
export function PageHeader({
  title,
  description,
  backTo,
  onBack,
  actions,
  className,
}: PageHeaderProps) {
  return (
    <header className={cn('flex flex-col gap-2', className)}>
      <div className="flex items-center gap-2">
        {backTo ? (
          <Link
            to={backTo}
            // Botão só-ícone: sem `aria-label` o leitor de tela anuncia só
            // "link". O `-ml-2` alinha o desenho da seta com a margem do texto
            // sem encolher o alvo de 44px.
            aria-label="Voltar"
            className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }), '-ml-2')}
          >
            <Icon as={ArrowBackIcon} size="lg" />
          </Link>
        ) : null}

        {!backTo && onBack ? (
          <button
            type="button"
            aria-label="Voltar"
            onClick={onBack}
            className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }), '-ml-2')}
          >
            <Icon as={ArrowBackIcon} size="lg" />
          </button>
        ) : null}

        <Text variant="pageTitle" as="h1" className="min-w-0 flex-1">
          {title}
        </Text>

        {actions ? <div className="flex shrink-0 items-center gap-1">{actions}</div> : null}
      </div>

      {description ? (
        <Text variant="body" tone="muted">
          {description}
        </Text>
      ) : null}
    </header>
  );
}
